import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { User, Calendar, FileText, Receipt, Gift, Award, Wallet, Edit2, Save, Copy, CheckCircle, XCircle, Video, BookOpen } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, isAuthenticated, setUser } = useAuth();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isHindi = i18n.language === 'hi';
  const [activeTab, setActiveTab] = useState('bookings');
  const [bookings, setBookings] = useState({ astrology_bookings: [], vastu_bookings: [] });
  const [reports, setReports] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [editing, setEditing] = useState(false);
  const [profileData, setProfileData] = useState({});
  const [referralCode, setReferralCode] = useState('');
  const [walletAmount, setWalletAmount] = useState(500);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [kundlis, setKundlis] = useState([]);

  const getToken = useCallback(() => localStorage.getItem('token'), []);

  const fetchBookings = useCallback(async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API}/user/bookings`, { headers: { Authorization: `Bearer ${token}` } });
      setBookings(res.data);
    } catch (err) { console.error(err); }
  }, [getToken]);

  const fetchReports = useCallback(async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API}/user/reports`, { headers: { Authorization: `Bearer ${token}` } });
      setReports(res.data);
    } catch (err) { console.error(err); }
  }, [getToken]);

  const fetchInvoices = useCallback(async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API}/user/invoices`, { headers: { Authorization: `Bearer ${token}` } });
      setInvoices(res.data);
    } catch (err) { console.error(err); }
  }, [getToken]);

  const fetchKundlis = useCallback(async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API}/user/kundlis`, { headers: { Authorization: `Bearer ${token}` } });
      setKundlis(res.data);
    } catch (err) { console.error(err); }
  }, [getToken]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchBookings();
      fetchReports();
      fetchInvoices();
      fetchKundlis();
    }
  }, [isAuthenticated, fetchBookings, fetchReports, fetchInvoices, fetchKundlis]);

  useEffect(() => {
    if (user) {
      setProfileData({ name: user.name || '', phone: user.phone || '', dob: user.dob || '', time_of_birth: user.time_of_birth || '', place_of_birth: user.place_of_birth || '', gender: user.gender || '' });
    }
  }, [user]);

  if (!isAuthenticated) return <Navigate to="/login" />;

  const handleProfileSave = async () => {
    try {
      const token = getToken();
      const res = await axios.put(`${API}/user/profile`, profileData, { headers: { Authorization: `Bearer ${token}` } });
      setUser(res.data);
      setEditing(false);
    } catch (err) { console.error(err); }
  };

  const handleRedeemReferral = async () => {
    try {
      const token = getToken();
      await axios.post(`${API}/referral/redeem`, { referral_code: referralCode }, { headers: { Authorization: `Bearer ${token}` } });
      alert(isHindi ? 'रेफरल कोड लागू!' : 'Referral code applied!');
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleCancelBooking = async (bookingId, bookingType) => {
    if (!window.confirm(isHindi ? '50% रिफंड 24+ घंटे पहले रद्द करने पर। 4 घंटे से कम में कोई रिफंड नहीं। क्या आप रद्द करना चाहते हैं?' : '50% refund if cancelled 24+ hrs before. No refund if less than 4 hrs. Confirm cancel?')) return;
    try {
      const token = getToken();
      await axios.post(`${API}/bookings/cancel`, { booking_id: bookingId, booking_type: bookingType }, { headers: { Authorization: `Bearer ${token}` } });
      alert(isHindi ? 'बुकिंग रद्द हो गई' : 'Booking cancelled');
      fetchBookings();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleWalletRecharge = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const res = await axios.post(`${API}/wallet/recharge`, { amount: walletAmount }, { headers: { Authorization: `Bearer ${token}` } });
      const { razorpay_order_id, razorpay_key_id } = res.data;
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        const rzp = new window.Razorpay({
          key: razorpay_key_id, amount: walletAmount * 100, currency: 'INR',
          name: 'Astrology Times', description: 'Wallet Recharge',
          order_id: razorpay_order_id,
          handler: async (response) => {
            await axios.post(`${API}/wallet/recharge/verify?razorpay_payment_id=${response.razorpay_payment_id}&razorpay_signature=${response.razorpay_signature}&razorpay_order_id=${razorpay_order_id}&amount=${walletAmount}`, {}, { headers: { Authorization: `Bearer ${token}` } });
            alert('Wallet recharged!');
          },
          theme: { color: '#C9A84C' }
        });
        rzp.open();
      };
      document.body.appendChild(script);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(user?.referral_code || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const tabs = [
    { key: 'bookings', icon: Calendar, label: isHindi ? 'मेरी बुकिंग' : 'My Bookings' },
    { key: 'profile', icon: User, label: isHindi ? 'प्रोफाइल' : 'Profile' },
    { key: 'reports', icon: FileText, label: isHindi ? 'रिपोर्ट' : 'Reports' },
    { key: 'invoices', icon: Receipt, label: isHindi ? 'चालान' : 'Invoices' },
    { key: 'referral', icon: Gift, label: isHindi ? 'रेफरल' : 'Referral' },
    { key: 'wallet', icon: Wallet, label: isHindi ? 'वॉलेट' : 'Wallet' },
    { key: 'kundli', icon: BookOpen, label: isHindi ? 'कुंडली' : 'Kundli' }
  ];

  const statusColors = { pending: 'text-yellow-400', confirmed: 'text-green-400', completed: 'text-blue-400', cancelled: 'text-red-400' };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-24 px-6 md:px-12 lg:px-24 pb-16" data-testid="user-dashboard">
      <SEO page="home" />
      <h1 className="text-4xl font-serif text-[#F5F2EB] mb-8">
        {isHindi ? `नमस्ते, ${user?.name}` : `Welcome, ${user?.name}`}
      </h1>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex flex-col items-center gap-2 p-4 border transition-all ${activeTab === tab.key ? 'border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]' : 'border-[#C9A84C]/15 bg-[#1A1A1A] text-[#A3A3A3] hover:border-[#C9A84C]/30'}`}
            data-testid={`tab-${tab.key}`}>
            <tab.icon className="w-5 h-5" />
            <span className="text-xs uppercase tracking-wider">{tab.label}</span>
          </button>
        ))}
      </div>

      {activeTab === 'bookings' && (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6">
              <p className="text-[#A3A3A3] text-sm">{isHindi ? 'कुल बुकिंग' : 'Total Bookings'}</p>
              <p className="text-3xl font-bold text-[#C9A84C]">{bookings.astrology_bookings.length + bookings.vastu_bookings.length}</p>
            </div>
            <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6">
              <p className="text-[#A3A3A3] text-sm">{isHindi ? 'लॉयल्टी पॉइंट्स' : 'Loyalty Points'}</p>
              <p className="text-3xl font-bold text-[#C9A84C]"><Award className="inline w-6 h-6 mr-1" />{user?.loyalty_points || 0}</p>
            </div>
            <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6">
              <p className="text-[#A3A3A3] text-sm">{isHindi ? 'वॉलेट बैलेंस' : 'Wallet Balance'}</p>
              <p className="text-3xl font-bold text-[#C9A84C]">₹{user?.wallet_balance || 0}</p>
            </div>
          </div>

          <h2 className="text-xl font-serif text-[#F5F2EB] mb-4">{isHindi ? 'ज्योतिष बुकिंग' : 'Astrology Bookings'}</h2>
          {bookings.astrology_bookings.length === 0 ? (
            <p className="text-[#A3A3A3] mb-6">{isHindi ? 'कोई बुकिंग नहीं' : 'No bookings yet'}</p>
          ) : (
            <div className="space-y-3 mb-8">
              {bookings.astrology_bookings.map(b => (
                <div key={b.booking_id} className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-[#F5F2EB] font-medium">{b.package_type} - {b.booking_date}</p>
                      <p className="text-[#A3A3A3] text-sm">{b.booking_time} | ₹{b.amount}</p>
                    </div>
                    <span className={`text-sm uppercase ${statusColors[b.status] || 'text-[#A3A3A3]'}`}>{b.status}</span>
                  </div>
                  <div className="flex gap-3 mt-3 flex-wrap">
                    {b.meet_link && (
                      <a href={b.meet_link} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 bg-green-600 text-white px-3 py-1 text-xs hover:bg-green-700" data-testid={`join-meet-${b.booking_id}`}>
                        <Video className="w-3 h-3" /> {isHindi ? 'मीटिंग जॉइन करें' : 'Join Meeting'}
                      </a>
                    )}
                    {(b.status === 'pending' || b.status === 'confirmed') && (
                      <button onClick={() => handleCancelBooking(b.booking_id, 'astrology')}
                        className="flex items-center gap-1 text-red-400 border border-red-400/30 px-3 py-1 text-xs hover:bg-red-400/10" data-testid={`cancel-booking-${b.booking_id}`}>
                        <XCircle className="w-3 h-3" /> {isHindi ? 'रद्द करें' : 'Cancel'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <h2 className="text-xl font-serif text-[#F5F2EB] mb-4">{isHindi ? 'वास्तु बुकिंग' : 'Vastu Bookings'}</h2>
          {bookings.vastu_bookings.length === 0 ? (
            <p className="text-[#A3A3A3]">{isHindi ? 'कोई बुकिंग नहीं' : 'No bookings yet'}</p>
          ) : (
            <div className="space-y-3">
              {bookings.vastu_bookings.map(b => (
                <div key={b.booking_id} className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-[#F5F2EB] font-medium">{b.service_type} - {b.booking_date}</p>
                      <p className="text-[#A3A3A3] text-sm">{b.booking_time} | ₹{b.amount}</p>
                    </div>
                    <span className={`text-sm uppercase ${statusColors[b.status] || 'text-[#A3A3A3]'}`}>{b.status}</span>
                  </div>
                  <div className="flex gap-3 mt-3 flex-wrap">
                    {b.meet_link && (
                      <a href={b.meet_link} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 bg-green-600 text-white px-3 py-1 text-xs hover:bg-green-700">
                        <Video className="w-3 h-3" /> {isHindi ? 'मीटिंग जॉइन करें' : 'Join Meeting'}
                      </a>
                    )}
                    {(b.status === 'pending' || b.status === 'confirmed') && (
                      <button onClick={() => handleCancelBooking(b.booking_id, 'vastu')}
                        className="flex items-center gap-1 text-red-400 border border-red-400/30 px-3 py-1 text-xs hover:bg-red-400/10">
                        <XCircle className="w-3 h-3" /> {isHindi ? 'रद्द करें' : 'Cancel'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'profile' && (
        <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6 max-w-xl">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-serif text-[#F5F2EB]">{isHindi ? 'मेरी प्रोफाइल' : 'My Profile'}</h2>
            <button onClick={() => editing ? handleProfileSave() : setEditing(true)}
              className="flex items-center gap-2 text-[#C9A84C] hover:text-[#E5C870]">
              {editing ? <><Save className="w-4 h-4" /> {isHindi ? 'सेव' : 'Save'}</> : <><Edit2 className="w-4 h-4" /> {isHindi ? 'संपादित' : 'Edit'}</>}
            </button>
          </div>
          <div className="space-y-4">
            {[
              { label: 'Name', key: 'name' },
              { label: 'Phone', key: 'phone' },
              { label: 'Date of Birth', key: 'dob', type: 'date' },
              { label: 'Time of Birth', key: 'time_of_birth', type: 'time' },
              { label: 'Place of Birth', key: 'place_of_birth' },
              { label: 'Gender', key: 'gender' }
            ].map(field => (
              <div key={field.key}>
                <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">{field.label}</label>
                {editing ? (
                  field.key === 'gender' ? (
                    <select value={profileData[field.key] || ''} onChange={(e) => setProfileData({ ...profileData, [field.key]: e.target.value })}
                      className="w-full bg-transparent border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none">
                      <option value="">Select</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  ) : (
                    <input type={field.type || 'text'} value={profileData[field.key] || ''} onChange={(e) => setProfileData({ ...profileData, [field.key]: e.target.value })}
                      className="w-full bg-transparent border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" />
                  )
                ) : (
                  <p className="text-[#F5F2EB] py-2 border-b border-[#C9A84C]/10">{profileData[field.key] || '-'}</p>
                )}
              </div>
            ))}
            <div>
              <label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Email</label>
              <p className="text-[#F5F2EB] py-2">{user?.email}</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'reports' && (
        <div>
          <h2 className="text-xl font-serif text-[#F5F2EB] mb-4">{isHindi ? 'सत्र रिपोर्ट' : 'Session Reports'}</h2>
          {reports.length === 0 ? (
            <p className="text-[#A3A3A3]">{isHindi ? 'कोई रिपोर्ट नहीं' : 'No reports yet'}</p>
          ) : reports.map(r => (
            <div key={r.report_id} className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-4 mb-3">
              <p className="text-[#F5F2EB]">{r.report_id} - {r.booking_type}</p>
              <p className="text-[#A3A3A3] text-sm">{r.created_at}</p>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'invoices' && (
        <div>
          <h2 className="text-xl font-serif text-[#F5F2EB] mb-4">{isHindi ? 'मेरे चालान' : 'My Invoices'}</h2>
          {invoices.length === 0 ? (
            <p className="text-[#A3A3A3]">{isHindi ? 'कोई चालान नहीं' : 'No invoices yet'}</p>
          ) : invoices.map(inv => (
            <div key={inv.invoice_id} className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-4 mb-3 flex justify-between items-center">
              <div>
                <p className="text-[#F5F2EB]">{inv.invoice_id}</p>
                <p className="text-[#A3A3A3] text-sm">₹{inv.amount} | {inv.created_at}</p>
              </div>
              <span className="text-[#C9A84C] text-sm">PDF</span>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'referral' && (
        <div className="max-w-xl">
          <h2 className="text-xl font-serif text-[#F5F2EB] mb-6">{isHindi ? 'दोस्त को रेफर करें' : 'Refer a Friend'}</h2>
          <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6 mb-6">
            <p className="text-[#A3A3A3] mb-3">{isHindi ? 'आपका रेफरल कोड' : 'Your Referral Code'}</p>
            <div className="flex items-center gap-3">
              <span className="text-[#C9A84C] text-2xl font-bold tracking-wider">{user?.referral_code}</span>
              <button onClick={copyReferralCode} className="text-[#A3A3A3] hover:text-[#C9A84C]">
                {copied ? <CheckCircle className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-[#A3A3A3] text-sm mt-3">
              {isHindi ? 'दोस्त को ₹200 की छूट, आपको ₹300 का क्रेडिट' : 'Friend gets ₹200 off, you get ₹300 credit'}
            </p>
          </div>

          {/* How Referral Works */}
          <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6 mb-6">
            <h3 className="text-[#F5F2EB] font-medium mb-4">{isHindi ? 'रेफरल कैसे काम करता है?' : 'How Referral Works?'}</h3>
            <div className="space-y-3 text-[#A3A3A3] text-sm">
              <p>1. {isHindi ? 'अपना रेफरल कोड कॉपी करें और दोस्त को भेजें (WhatsApp, SMS आदि)' : 'Copy your referral code above and share with friends (WhatsApp, SMS etc.)'}</p>
              <p>2. {isHindi ? 'दोस्त अपना अकाउंट बनाएं और Dashboard → Referral → "Redeem" में आपका कोड डालें' : 'Friend creates account and enters your code in Dashboard → Referral → "Redeem"'}</p>
              <p>3. {isHindi ? 'दोस्त को पहली बुकिंग पर ₹200 की छूट मिलेगी' : 'Friend gets ₹200 off on their first booking'}</p>
              <p>4. {isHindi ? 'दोस्त के भुगतान के बाद आपके वॉलेट में ₹300 जुड़ जाएंगे' : 'After friend pays, ₹300 is added to YOUR wallet'}</p>
            </div>
            <div className="mt-4">
              <button onClick={() => {
                const msg = encodeURIComponent(`Join Astrology Times! Use my referral code ${user?.referral_code} and get Rs.200 OFF on your first consultation. Book now: https://astrologytimes.co.in`);
                window.open(`https://wa.me/?text=${msg}`, '_blank');
              }} className="bg-[#25D366] text-white px-6 py-2 text-sm hover:bg-[#20BA5A] flex items-center gap-2">
                {isHindi ? 'WhatsApp पर शेयर करें' : 'Share on WhatsApp'}
              </button>
            </div>
          </div>
          <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6">
            <p className="text-[#F5F2EB] mb-3">{isHindi ? 'रेफरल कोड रिडीम करें' : 'Redeem a Referral Code'}</p>
            <div className="flex gap-3">
              <input value={referralCode} onChange={(e) => setReferralCode(e.target.value)}
                className="flex-1 bg-transparent border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none"
                placeholder="Enter code" data-testid="referral-code-input" />
              <button onClick={handleRedeemReferral} className="bg-[#C9A84C] text-[#0A0A0A] px-6 py-2 hover:bg-[#E5C870]">
                {isHindi ? 'रिडीम' : 'Redeem'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'wallet' && (
        <div className="max-w-xl">
          <h2 className="text-xl font-serif text-[#F5F2EB] mb-6">{isHindi ? 'वॉलेट' : 'Wallet'}</h2>
          <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6 mb-6 text-center">
            <p className="text-[#A3A3A3] mb-2">{isHindi ? 'वर्तमान बैलेंस' : 'Current Balance'}</p>
            <p className="text-5xl font-bold text-[#C9A84C]">₹{user?.wallet_balance || 0}</p>
            <p className="text-[#A3A3A3] text-sm mt-2">{isHindi ? 'प्रति मिनट सत्रों के लिए उपयोग करें' : 'Use for per-minute sessions'}</p>
          </div>
          <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6">
            <p className="text-[#F5F2EB] mb-3">{isHindi ? 'वॉलेट रिचार्ज' : 'Recharge Wallet'}</p>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[500, 1000, 2000].map(amt => (
                <button key={amt} onClick={() => setWalletAmount(amt)}
                  className={`py-3 border text-center ${walletAmount === amt ? 'border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]' : 'border-[#C9A84C]/15 text-[#A3A3A3]'}`}>
                  ₹{amt}
                </button>
              ))}
            </div>
            <button onClick={handleWalletRecharge} disabled={loading}
              className="w-full bg-[#C9A84C] text-[#0A0A0A] py-3 font-medium hover:bg-[#E5C870] disabled:opacity-40"
              data-testid="wallet-recharge-btn">
              {isHindi ? `₹${walletAmount} रिचार्ज करें` : `Recharge ₹${walletAmount}`}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'kundli' && (
        <div>
          <h2 className="text-xl font-serif text-[#F5F2EB] mb-4">{isHindi ? 'मेरी कुंडलियां' : 'My Saved Kundlis'}</h2>
          {kundlis.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-[#A3A3A3] mb-4">{isHindi ? 'कोई कुंडली नहीं' : 'No saved Kundlis yet'}</p>
              <button onClick={() => navigate('/kundli')} className="bg-[#C9A84C] text-[#0A0A0A] px-6 py-2 hover:bg-[#E5C870]">
                {isHindi ? 'कुंडली बनाएं' : 'Generate Kundli'}
              </button>
            </div>
          ) : kundlis.map(k => (
            <details key={k.kundli_id} className="bg-[#1A1A1A] border border-[#C9A84C]/10 mb-3">
              <summary className="p-4 cursor-pointer text-[#F5F2EB] hover:text-[#C9A84C]">
                {k.name} - {k.dob} ({k.place_of_birth})
                <span className="text-[#A3A3A3] text-xs ml-2">{k.created_at?.substring(0, 10)}</span>
              </summary>
              <div className="p-4 border-t border-[#C9A84C]/10 text-[#F5F2EB] text-sm whitespace-pre-wrap leading-relaxed">
                {k.kundli_content}
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
